<script>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { slugify, } from '@/assets/js/src/util/slugify'
import cookieManager from '@/assets/js/src/util/cookieManagerWrapper'
import { safeDecodeURI, } from '@/assets/js/src/util/safeDecodeURI'
import { useHelpItemApi, } from '@/assets/js/src/modules/help/_composables/useHelpItemApi'
import { mapActions, mapWritableState, } from 'pinia'
import { useHelpStore, } from '@/assets/js/src/modules/help/_pinia/help'
import { useHandleRoutableLink, } from '@/assets/js/src/util/composables/useHandleRoutableLink'
import LoadingDummyH1 from '@/assets/js/src/components/loading/LoadingDummyH1'
import LoadingDummyP from '@/assets/js/src/components/loading/LoadingDummyP'
import { useHelpItemStore, } from '@/assets/js/src/modules/help/_pinia/helpItem'
import { icons, } from '@/assets/js/src/vuetify/iconMap'
import { defineAsyncComponent, } from 'vue'
import { useTranslation, } from '@/assets/js/src/util/composables'
import { useItemHeadInfo, } from '@/assets/js/src/modules/help/_composables/useItemHeadInfo'

const HelpFeedback = defineAsyncComponent(() => import('@/assets/js/src/modules/help/_components/HelpFeedback.vue'))
const FEEDBACK_COOKIE = 'bs-help-feedback'
const HITS_COOKIE = 'bs-help-hits'

export default {
    name: 'HelpItem',
    components: { LoadingDummyP, LoadingDummyH1, Overlay, HelpFeedback, },
    setup () {
        const {
            helpItem,
            children,
            messagesLoaded,
            asyncStatusLoaded,
            scrollTo,
        } = useHelpItemApi()

        return {
            helpItem,
            children,
            messagesLoaded,
            asyncStatusLoaded,
            scrollTo,
            ...useItemHeadInfo({ asyncStatusLoaded,helpItem, }),
            ...useHandleRoutableLink(),
            ...useTranslation(),
        }
    },
    data: () => ({
        notSent: false,
    }),
    computed: {
        ...mapWritableState(useHelpStore,[
            'backToList',
        ]),
        related: function () {
            if (!this.asyncStatusLoaded) {
                return []
            }

            let related = globalThis.clone(this.helpItem.related_items)
            let helpItemCategories = globalThis.clone(this.helpItem.categories)
            let relatedByCategory = {}
            let listedItems = []

            related.forEach((elem) => {
                helpItemCategories.forEach((category) => {
                    let categories = []
                    elem.categories.forEach((val) => {
                        return categories.push(val.id)
                    })

                    if (listedItems.indexOf(elem.id) === -1 && categories.indexOf(category.id) !== -1) {
                        if (typeof relatedByCategory['category_' + category.id] === 'undefined') {
                            relatedByCategory['category_' + category.id] = category
                            relatedByCategory['category_' + category.id].items = []
                        }
                        relatedByCategory['category_' + category.id].items.push({
                            ...elem,
                            relatedUrl: this.getRelatedUrl(elem),
                        })
                        listedItems.push(elem.id)
                    }
                })
            })

            relatedByCategory = Object.values(relatedByCategory)
            relatedByCategory.sort((a, b) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            })

            return relatedByCategory
        },
        customHelpItem: function () {
            return {
                ...this.helpItem,
                replacedMessage: this.replace(this.helpItem.message),
            }
        },
        customChildren: function () {
            return this.children.map((child)=>{
                return {
                    ...child,
                    slug: slugify(child.title),
                    replacedMessage: this.replace(child.message),
                }
            })
        },
    },
    watch: {
        '$route': {
            immediate: false,
            handler: function (to, from) {
                if(to.params.id === from.params.id && to.hash && to.hash !== from.hash) {
                    let elem = document.getElementById(to.hash.replace('#', ''))
                    elem && window.scrollTo(0, parseInt(elem.offsetTop) - (parseInt(document.querySelector('.bs-header-wrapper').clientHeight) + 15))
                }
            },
        },
        asyncStatusLoaded: {
            immediate: true,
            handler: async function (newVal, oldVal) {
                if (newVal !== oldVal && newVal && this.scrollTo.trim() !== '') {
                    let diff = 0
                    let start = Date.now()
                    while (document.getElementById(this.scrollTo) === null && diff < 5000) {
                        await new Promise(resolve => setTimeout(resolve))
                        diff = Date.now() - start
                    }
                    let elem = document.getElementById(this.scrollTo)
                    window.scrollTo(0, parseInt(elem.offsetTop) - (parseInt(document.querySelector('.bs-header-wrapper').clientHeight) + 15))
                }
            },
        },
    },
    mounted () {
        if (this.$route.hash && this.$route.hash.trim() !== '') {
            this.scrollTo = safeDecodeURI(this.$route.hash.replace('#', ''))
        }

        // Hits prüfen
        let hits = []
        if (cookieManager.get(HITS_COOKIE)) {
            hits = JSON.parse(cookieManager.get(HITS_COOKIE))
        }

        if (!hits.includes(this.$route.params.id)) {
            this.sendHit({ id: this.$route.params.id, })
            hits.push(this.$route.params.id)
            cookieManager.set(HITS_COOKIE, JSON.stringify(hits), { expires: 365, })
        }

        // Feedback prüfen
        let feedback = []
        if (cookieManager.get(FEEDBACK_COOKIE)) {
            feedback = JSON.parse(cookieManager.get(FEEDBACK_COOKIE))
        }

        this.notSent = !feedback.includes(this.$route.params.id)
    },
    methods: {
        ...mapActions(useHelpItemStore,[
            'sendHit',
        ]),
        getRelatedUrl (item) {
            if (item.parent) {
                return '/help/' + slugify(item.parent.title) + '/' + item.parent.id + '#' + slugify(item.title)
            }

            return '/help/' + slugify(item.title) + '/' + item.id
        },
        handleLink (event) {
            event.preventDefault()
            this.backToList = false
        },
        replace (value) {
            if (typeof value === 'undefined') {
                return ''
            }

            // Slugify Links
            let reg = new RegExp(/###(.[^#]*)###/mg)
            let matches = value.match(reg)

            if (matches !== null) {
                matches.forEach((elem) => {
                    let slug = elem.substr(3, elem.length - 6)

                    if (slug.indexOf('#') !== -1) {
                        value = value.replace(elem, '#' + slugify(slug.replace('#', '').trim()))
                    } else {
                        value = value.replace(elem, slugify(slug))
                    }

                })
            }

            // Icons ersetzen
            let regIcon = new RegExp(/\$\$(.[^$]*)\$\$/mg)
            let matchesIcon = value.match(regIcon)

            if (matchesIcon !== null) {
                let check = matchesIcon.filter((elem) => {
                    return elem.indexOf('GoogleChrome') !== -1
                })

                if (check.length > 0) {
                    matchesIcon.forEach((elem) => {
                        let part = elem.substr(2, elem.length - 4).replace('Browser', '')
                        if (icons['mdi' + part]) {
                            value = value.replace(elem, `<svg class="${part.toLowerCase()}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="${icons['mdi' + part].props.path}"/></svg></div><div>`)
                        } else {
                            value = value.replace(elem, '</div><div>')
                        }
                    })
                    value = value.replace('<ul>', '<ul class="help-item__text__browser-list">')
                    value = value.replace(/<li>/gm, '<li><div class="help-item__text__browser-list__block"><div>')
                    value = value.replace(/<\/li>/gm, '</div></div></li>')
                } else {
                    matchesIcon.forEach((elem) => {
                        let part = elem.substr(2, elem.length - 4)
                        if (icons[part]) {
                            value = value.replace(elem, `<svg class="${part.toLowerCase()}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="${icons[part].props.path}"/></svg>`)
                        } else {
                            value = value.replace(elem, '')
                        }
                    })
                }
            }

            // Icons ersetzen
            value = value.replace(/§div§/mg, '<div class="help-item__text__hint">')
            value = value.replace(/§\/div§/mg, '</div>')

            return value
        },
    },
}
</script>

<template>
    <overlay
        class-name="help-item"
    >
        <v-row
            no-gutters
        >
            <v-col
                cols="12"
                md="9"
                class="pr-0 pr-md-6"
            >
                <transition
                    name="fade"
                    mode="out-in"
                >
                    <div
                        v-if="messagesLoaded && asyncStatusLoaded"
                        :key="'trans-wrapper' + customHelpItem.id"
                    >
                        <div class="d-flex mb-4 hidden-print-only">
                            <!-- BACK BTN -->
                            <v-btn
                                v-if="backToList"
                                class="help-item__back-btn arrow-icon--direction"
                                exact
                                icon
                                variant="text"
                                :title="t('async.help.back_btn')"
                                tag="a"
                                to="/help"
                                @click="handleLink"
                            >
                                <v-icon>
                                    bs:$vuetify.icons.mdiArrowLeft
                                </v-icon>
                            </v-btn>
                            <v-btn
                                v-else
                                class="help-item__back-btn"
                                exact
                                icon
                                variant="text"
                                tag="a"
                                :title="t('async.help.overview_btn')"
                                to="/help"
                                @click="handleLink"
                            >
                                <v-icon>
                                    bs:$vuetify.icons.mdiHelpCircle
                                </v-icon>
                            </v-btn>

                            <!-- TITLE -->
                            <h1>
                                {{ customHelpItem.title }}
                            </h1>
                        </div>
                        <div
                            :key="'text_' + customHelpItem.id"
                            class="help-item__text"
                            @click="handleRoutableLink"
                            v-html="customHelpItem.replacedMessage"
                        ></div>
                        <template
                            v-for="child in customChildren"
                            :key="'heading_child_' + child.id"
                        >
                            <h2 :id="child.slug">
                                {{ child.title }}
                            </h2>
                            <div
                                class="help-item__text"
                                @click="handleRoutableLink"
                                v-html="child.replacedMessage"
                            ></div>
                        </template>
                    </div>
                    <div
                        v-else
                        :key="'trans-dummy-wrapper'"
                    >
                        <loading-dummy-h1></loading-dummy-h1>
                        <loading-dummy-p
                            count="4"
                            height="18px"
                            margin="8px 0"
                            key-prefix="dummy_2"
                        ></loading-dummy-p>
                        <loading-dummy-p
                            count="5"
                            height="18px"
                            margin="8px 0"
                            key-prefix="dummy_3"
                        ></loading-dummy-p>
                        <loading-dummy-p
                            count="4"
                            height="18px"
                            margin="8px 0"
                            key-prefix="dummy_4"
                        ></loading-dummy-p>
                    </div>
                </transition>
                <transition
                    name="fade"
                    mode="out-in"
                >
                    <help-feedback
                        v-if="notSent"
                        key="feedback"
                        :loaded="messagesLoaded && asyncStatusLoaded"
                    ></help-feedback>
                </transition>
            </v-col>
            <transition
                name="fade"
                mode="out-in"
            >
                <v-col
                    v-if="messagesLoaded && asyncStatusLoaded && (customHelpItem.related_items || []).length !== 0"
                    cols="12"
                    md="3"
                    class="help-item__related"
                >
                    <template
                        v-for="rel in related"
                        :key="'heading_related_' + rel.id"
                    >
                        <h3>
                            {{ t('async.help.related',{category: rel.name,}) }}
                        </h3>
                        <ul>
                            <li
                                v-for="relItem in rel.items"
                                :key="'rel_item_' + relItem.id"
                            >
                                <router-link
                                    :to="{path: relItem.relatedUrl,}"
                                    :title="relItem.title"
                                >
                                    {{ relItem.title }}
                                </router-link>
                            </li>
                        </ul>
                    </template>
                </v-col>
                <v-col
                    v-else
                    cols="12"
                    md="3"
                    class="help-item__related"
                >
                    <loading-dummy-p
                        count="8"
                        width="100%"
                        height="18px"
                        margin="8px 0"
                        key-prefix="dummy_5"
                    ></loading-dummy-p>
                </v-col>
            </transition>
        </v-row>
    </overlay>
</template>

<style lang="scss">
@use "@/assets/js/src/style/elements/btnBack";

.bs-app .help-item {
    @include btnBack.btn-back;

    .slideDown-enter-active, .slideDown-leave-active {
        transition: all .5s ease-in-out;
    }

    .slideDown-enter-to, .slideDown-leave-from {
        transform: scale(1);
    }

    .slideDown-enter-from, .slideDown-leave-to {
        transform: scale(0);
    }

    h2 {
        margin: 24px 0 16px;
        font-size: 1.3em;
    }

    &__related {
        padding: 0 0 0 8px;

        h3 {
            margin: 16px 0;
            color: var(--bs-color-overlay-grey-dark);
            font-weight: 400;
            font-size: 16px;
        }

        ul {
            margin: 0;
            padding: 0;
            list-style: none;

            li {
                margin-bottom: 8px;

                a {
                    color: var(--bs-color-overlay-grey-dark);
                    transition: all .2s ease-in-out;

                    &:hover {
                        color: map-deep-get($bs-color, bsHighlight);
                    }
                }
            }
        }
    }

    &__text {
        kbd {
            color: var(--bs-color-overlay-grey-dark);
            background: var(--bs-color-white);
            border: 1px solid var(--bs-color-overlay-grey-dark);
        }

        svg {
            fill: currentColor;
        }

        & > div {
            display: flex;
            flex-wrap: nowrap;
            margin: 0 0 16px;

            @media (max-width: #{map-deep-get($bs-xs, breakpointValueHalf)}px) {
                flex-wrap: wrap;
            }

            > div:first-of-type {
                width: 275px;
                margin: 0 8px 0 0;
                line-height: 19px;

                @media (max-width: #{map-deep-get($bs-xs, breakpointValueHalf)}px) {
                    width: 100%;
                    margin: 0;
                }
            }

            > div:last-of-type {
                width: calc(100% - 283px);
                line-height: 19px;

                @media (max-width: #{map-deep-get($bs-xs, breakpointValueHalf)}px) {
                    width: 100%;
                    margin: 4px 0 0;
                }
            }
        }

        & > .help-item__text__hint {
            display: block;
            padding: 16px;
            background: var(--bs-color-white);

            h4 {
                position: relative;
                margin: 0 0 16px;
                padding: 0 0 0 30px;
                font-weight: bold;
                font-size: 16px;

                svg {
                    position: absolute;
                    top: calc(50% - 2px);
                    left: 0;
                    width: 24px;
                    height: 24px;
                    transform: translateY(-50%);
                }
            }
        }

        &__browser-list {
            display: flex;
            flex-wrap: wrap;
            margin: 0;
            padding: 0;
            list-style: none;


            & > li {
                width: calc(100% / 2);
                margin: 0 0 16px;

                @media (width <= 600px) {
                    width: calc(100% / 2);
                }

                @media (max-width: #{map-deep-get($bs-xs, breakpointValueHalf)}px) {
                    width: 100%;
                }

                .help-item__text__browser-list__block {
                    display: flex;
                    flex-wrap: nowrap;

                    & > div:first-of-type {
                        width: 48px;
                        height: 48px;
                        margin-right: 12px;

                        & > svg {
                            display: block;
                            width: 48px;
                            height: 48px;
                            padding: 3px;
                            color: map-deep-get($bs-color, white);
                            border-radius: 48px;
                            fill: currentColor;

                            &.googlechrome, &.firefox, &.applesafari, &.edge, &.opera {
                                background: #4caf50;
                            }

                            &.internetexplorer {
                                background: #ff5252;
                            }
                        }
                    }

                    & > div:last-of-type {
                        width: auto;
                        height: auto;
                        margin: 0;

                        span {
                            color: var(--bs-color-overlay-grey-dark);
                        }
                    }
                }
            }
        }
    }
}
</style>
